import {
  CUSTOMERIO_TO,
  CUSTOMERIO_TRANSACTIONAL_MESSAGE_ID,
  CUSTOMERIO_IDENTIFIERS,
  CUSTOMER_IO_BODY,
  CUSTOMER_IO_FROM,
  CUSTOMER_IO_SUBJECT,
  CUSTOMER_IO_ATTACHMENTS,
  CUSTOMER_IO_BCC,
  CUSTOMER_IO_DISABLE_CSS_PREPROCESSING,
  CUSTOMER_IO_DISABLE_MESSAGE_RETENTION,
  CUSTOMER_IO_FAKE_BCC,
  CUSTOMER_IO_HEADERS,
  CUSTOMER_IO_MESSAGE_DATA,
  CUSTOMER_IO_PLAINTEXT_BODY,
  CUSTOMER_IO_PREHEADER,
  CUSTOMER_IO_QUEUE_DRAFT,
  CUSTOMER_IO_REPLY_TO,
  CUSTOMER_IO_SEND_AT,
  CUSTOMER_IO_SEND_TO_UNSUBSCRIBED,
  CUSTOMER_IO_TRACKED,
  CUSTOMER_IO_ID,
  CUSTOMER_IO_EMAIL,
  CUSTOMER_IO_CIO_ID,
} from "@avenueops/shared-types";

interface Props {
  fullFieldName: string;
}

export function getCustomerIoFieldDescription(fullFieldName: string): JSX.Element | null {
  switch (fullFieldName) {
    case CUSTOMERIO_TO:
      return (
        <span>
          The message recipient(s). Supports multiple addresses separated by commas. Your request can contain up to 15 total recipients between the{" "}
          <code>to</code> and <code>bcc</code> keys. <br /> <br />
          You can include a display/friendly name in your from address, but we recommend that you use quotation marks around the friendly name to avoid
          potential issues with special characters, e.g.{" "}
          <strong>
            <code>{`\"Person\" <person@example.com>`}</code>
          </strong>
        </span>
      );
    case CUSTOMERIO_TRANSACTIONAL_MESSAGE_ID:
      return (
        <span>
          The transactional message template that you want to use for your message. You can call the template by its <strong>numerical ID</strong>
        </span>
      );
    case CUSTOMERIO_IDENTIFIERS:
      return (
        <span>
          Identifies the person represented by your transactional message by one of, and only one of, <code>id</code>, <code>email</code>, or{" "}
          <code>cio_id</code>
        </span>
      );
    case CUSTOMER_IO_BODY:
      return (
        <span>
          The HTML body of your message. This overrides the body of the transactional template (referenced by <code>transactional_message_id</code>)
        </span>
      );
    case CUSTOMER_IO_FROM:
      return (
        <span>
          The address that your email is from. This address must be verified by Customer.io. This overrides the from address set within the transactional
          template (referenced by <code>transactional_message_id</code>).
          <br /> <br />
          You can include a display/friendly name in your from address, but we recommend that you use quotation marks around the friendly name to avoid
          potential issues with special characters, e.g.{" "}
          <strong>
            <code>{`\"Person\" <person@example.com>`}</code>
          </strong>
        </span>
      );
    case CUSTOMER_IO_SUBJECT:
      return (
        <span>
          The subject line for your message. This overrides the subject of the transactional template (referenced by <code>transactional_message_id</code>)
        </span>
      );
    case CUSTOMER_IO_ATTACHMENTS:
      return (
        <span>
          A dictionary of attachments where the filename is the key and the value is the base64-encoded contents. The total size of all attachments must be less
          than 2 MB. Some filetype extensions are restricted.
        </span>
      );
    case CUSTOMER_IO_BCC:
      return (
        <span>
          Blind copy message recipients. Supports multiple addresses separated by commas. Your request can contain up to 15 total recipients between the{" "}
          <code>to</code> and <code>bcc</code> keys
        </span>
      );
    case CUSTOMER_IO_DISABLE_CSS_PREPROCESSING:
      return (
        <span>
          Set to <code>true</code> to disable CSS preprocessing. This setting overrides the CSS preprocessing setting on the{" "}
          <code>transactional_message_id</code> as set in the user interface. Transactional emails have CSS preprocessing enabled by default
        </span>
      );
    case CUSTOMER_IO_DISABLE_MESSAGE_RETENTION:
      return (
        <span>
          If true, the message body is not retained in delivery history. Setting this value overrides the value set in the settings of your{" "}
          <code>transactional_message_id</code>
        </span>
      );
    case CUSTOMER_IO_FAKE_BCC:
      return (
        <span>
          If true, rather than sending true copies to BCC addresses, Customer.io sends a copy of the message with the subject line containing the recipient
          address(es)
        </span>
      );
    case CUSTOMER_IO_HEADERS:
      return (
        <span>Headers must be strings and cannot contain any non-ASCII characters or empty spaces. Some headers are reserved and cannot be overwritten</span>
      );
    case CUSTOMER_IO_MESSAGE_DATA:
      return <span>An object containing the key-value pairs referenced using liquid in your message</span>;
    case CUSTOMER_IO_PLAINTEXT_BODY:
      return (
        <span>
          The plaintext body of your message. This overrides the body of your transactional template (referenced by <code>transactional_message_id</code>)
        </span>
      );
    case CUSTOMER_IO_PREHEADER:
      return <span>Also known as “preview text”, this is the block block of text that users see next to, or underneath, the subject line in their inbox</span>;
    case CUSTOMER_IO_QUEUE_DRAFT:
      return (
        <span>
          If true, your transactional message is held as a draft in Customer.io and not sent directly to your audience. You must go to the Deliveries and Drafts
          page to send your message
        </span>
      );
    case CUSTOMER_IO_REPLY_TO:
      return (
        <span>
          The address that recipients can reply to, if different from the <code>from</code> address
        </span>
      );
    case CUSTOMER_IO_SEND_AT:
      return (
        <span>
          A unix timestamp (seconds since epoch) determining when the message will be sent. The timestamp can be up to 90 days in the future. If this value is
          in the past your message is sent immediately
        </span>
      );
    case CUSTOMER_IO_SEND_TO_UNSUBSCRIBED:
      return (
        <span>
          If false, your message is not sent to unsubscribed recipients. Setting this value overrides the value set in the settings of your{" "}
          <code>transactional_message_id</code>
        </span>
      );
    case CUSTOMER_IO_TRACKED:
      return <span>If true, Customer.io tracks opens and link clicks in your message</span>;
    case CUSTOMER_IO_ID:
      return (
        <span>
          The identifier for the person represented by the transactional message. NOTE: If your workspace identifies people by email, use the <code>email</code>{" "}
          identifier instead
        </span>
      );
    case CUSTOMER_IO_EMAIL:
      return (
        <span>
          The identifier for the person represented by the transactional message. Use this option if your workspace identifies people by <code>email</code>{" "}
          rather than by <code>id</code>
        </span>
      );
    case CUSTOMER_IO_CIO_ID:
      return <span>A unique, immutable identifier for a person, set by Customer.io when you add a person</span>;
    default:
      return null;
  }
}
